import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

export type DsInputType = 'text' | 'email' | 'tel' | 'url' | 'password' | 'number' | 'search';
export type DsInputSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'ds-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsInputComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-input-wrapper" [class]="wrapperClasses()">
      @if (label()) {
        <label [for]="inputId" class="ds-input-label">
          {{ label() }}
          @if (required()) {
            <span aria-hidden="true" class="ds-input-required">*</span>
          }
        </label>
      }

      <div class="ds-input-shell" [class]="shellClasses()">
        @if (prefix()) {
          <span class="ds-input-adorn ds-input-prefix">{{ prefix() }}</span>
        }

        <input
          [id]="inputId"
          [type]="type()"
          [disabled]="isDisabled()"
          [required]="required()"
          [placeholder]="placeholder() ?? ''"
          [attr.aria-invalid]="!!error() || null"
          [attr.aria-describedby]="(hint() || error() || success()) ? inputId + '-help' : null"
          [(ngModel)]="value"
          (ngModelChange)="onValueChange($event)"
          class="ds-input-field"
          [class]="'ds-input-field--' + size()"
        />

        @if (suffix()) {
          <span class="ds-input-adorn ds-input-suffix">{{ suffix() }}</span>
        }
      </div>

      @if (hint() || error() || success()) {
        <div
          [id]="inputId + '-help'"
          class="ds-input-help"
          [class]="'ds-input-help--' + state()"
        >
          {{ error() || success() || hint() }}
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: contents;
    }

    .ds-input-wrapper {
      display: inline-flex;
      flex-direction: column;
      gap: 6px;
      font-family: var(--f-body);

      &.ds-input-wrapper--full {
        width: 100%;
      }
    }

    .ds-input-label {
      font-size: 13px;
      font-weight: 600;
      color: var(--on-surface);
      letter-spacing: 0.005em;
    }

    .ds-input-required {
      color: #CC2831;
      margin-left: 4px;
    }

    /* ── Shell ─────────────────────────────────────────────────── */
    .ds-input-shell {
      display: flex;
      align-items: stretch;
      background: var(--surface);
      border: 1.5px solid var(--border);
      border-radius: var(--r-sm);
      overflow: hidden;
      transition:
        border-color var(--d-2) var(--e-standard),
        box-shadow var(--d-2) var(--e-standard);

      &:focus-within {
        border-color: var(--c-focus);
        box-shadow: 0 0 0 3px rgba(166, 135, 255, 0.25);
        outline: none;
      }

      &.ds-input-shell--error {
        border-color: #CC2831;

        &:focus-within {
          border-color: #CC2831;
          box-shadow: 0 0 0 3px rgba(204, 40, 49, 0.20);
        }
      }

      &.ds-input-shell--success {
        border-color: #008A00;

        &:focus-within {
          border-color: #008A00;
          box-shadow: 0 0 0 3px rgba(0, 138, 0, 0.18);
        }
      }

      &.ds-input-shell--disabled {
        opacity: 0.55;
        background: var(--surface-secondary);
        cursor: not-allowed;
      }
    }

    /* ── Field ─────────────────────────────────────────────────── */
    .ds-input-field {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font-family: var(--f-body);
      color: var(--on-surface);
      letter-spacing: 0.005em;

      &::placeholder {
        color: var(--on-surface-muted);
      }

      &:disabled {
        cursor: not-allowed;
      }

      &--sm {
        padding: 6px 12px;
        font-size: 13px;
        min-height: 36px;
      }

      &--md {
        padding: 10px 14px;
        font-size: 14px;
        min-height: 44px;
      }

      &--lg {
        padding: 14px 18px;
        font-size: 16px;
        min-height: 52px;
      }
    }

    /* ── Adornments ────────────────────────────────────────────── */
    .ds-input-adorn {
      display: inline-flex;
      align-items: center;
      padding: 0 10px;
      color: var(--on-surface-muted);
      font-size: 13px;
      background: transparent;
      white-space: nowrap;
    }

    .ds-input-prefix {
      border-right: 1px solid var(--border-subtle);
    }

    .ds-input-suffix {
      border-left: 1px solid var(--border-subtle);
    }

    /* ── Help text ─────────────────────────────────────────────── */
    .ds-input-help {
      font-size: 12px;
      line-height: 1.4;

      &--default {
        color: var(--on-surface-muted);
      }

      &--error {
        color: #CC2831;
      }

      &--success {
        color: #008A00;
      }
    }
  `],
})
export class DsInputComponent implements ControlValueAccessor {
  readonly type = input<DsInputType>('text');
  readonly size = input<DsInputSize>('md');
  readonly label = input<string | undefined>(undefined);
  readonly hint = input<string | undefined>(undefined);
  readonly error = input<string | undefined>(undefined);
  readonly success = input<string | undefined>(undefined);
  readonly disabled = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly placeholder = input<string | undefined>(undefined);
  readonly prefix = input<string | undefined>(undefined);
  readonly suffix = input<string | undefined>(undefined);
  readonly fullWidth = input<boolean>(false);

  readonly inputId = `dsi-${Math.random().toString(36).slice(2, 8)}`;

  readonly isDisabled = signal(false);
  value = '';

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  readonly state = computed(() =>
    this.error() ? 'error' : this.success() ? 'success' : 'default'
  );

  readonly wrapperClasses = computed(() => {
    const parts: string[] = [];
    if (this.fullWidth()) parts.push('ds-input-wrapper--full');
    return parts.join(' ');
  });

  readonly shellClasses = computed(() => {
    const parts: string[] = [];
    const s = this.state();
    if (s !== 'default') parts.push(`ds-input-shell--${s}`);
    if (this.isDisabled() || this.disabled()) parts.push('ds-input-shell--disabled');
    return parts.join(' ');
  });

  onValueChange(val: string): void {
    this.value = val;
    this.onChange(val);
    this.onTouched();
  }

  writeValue(value: string): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
